import { QueryKeys, ThreeMinutes } from "@Constants";
import {
    IAnswer,
    IQuestionnaireInstance,
    IQuestionnaireInstanceMinimal,
    IQuestionnaireInstanceSchema,
} from "@Models/IQuestionnaireDetails";
import QuestionnaireService from "@Services/QuestionnaireService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getUserOption } from "@Utils";
import { useMemo } from "react";

/**
 * A hook for a single Questionnaire Instance.
 */
const useQuestionnaireInstance = ({
    activityCategoryId,
    activityId,
    activityInstanceId,
    selectedQuestionnaireId,
}: {
    activityCategoryId?: string;
    activityId?: string;
    activityInstanceId?: string;
    selectedQuestionnaireId?: string;
}) => {
    // ** Hooks ** //
    const cmeOption = getUserOption("enable-cme-points-tracking");
    const queryClient = useQueryClient();

    // ** Services ** //
    const questionnaireService = new QuestionnaireService();

    // ** Queries ** //

    const fetchQuestionnaireAssociations = async (
        activityId?: string,
        activityCategoryId?: string,
        activeOnly: boolean = false
    ): Promise<IQuestionnaireInstanceMinimal[]> => {
        if (!activityId && !activityCategoryId) {
            return [];
        }

        const response = await questionnaireService.getAssociations(activityId, activityCategoryId, activeOnly);

        if (!response.hasErrors) {
            return response.value.associatedQuestionnaires;
        }
        return [];
    };

    const { data: questionnaireAssociations, isFetching: isFetchingMinimalQuestionnaires } = useQuery<
        IQuestionnaireInstanceMinimal[]
    >(
        [QueryKeys.QuestionnaireAssociationList, activityId, activityCategoryId, "all"],
        () => fetchQuestionnaireAssociations(activityId, activityCategoryId, false),
        {
            enabled: cmeOption === "true", // only enable if CME is enabled
            placeholderData: [],
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
        }
    );

    const { data: questionnaireAssociationsActiveOnly } = useQuery<IQuestionnaireInstanceMinimal[]>(
        [QueryKeys.QuestionnaireAssociationList, activityId, activityCategoryId, "activeOnly"],
        () => fetchQuestionnaireAssociations(activityId, activityCategoryId, true),
        {
            enabled: cmeOption === "true", // only enable if CME is enabled
            placeholderData: [],
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
        }
    );

    const questionnaireIds = useMemo(
        () => questionnaireAssociations?.map((qa) => qa.questionnaireId) ?? [],
        [questionnaireAssociations]
    );

    const { data: instance, isFetching: isFetchingQuestionnaireInstance } = useQuery<
        IQuestionnaireInstance | undefined
    >(
        [QueryKeys.QuestionnaireInstanceCurrent, questionnaireIds, activityInstanceId],
        async () => {
            // this guard clause is not needed, since the query is disabled when the questionnaireIds are empty
            if (questionnaireIds.length === 0 || !activityInstanceId) {
                return undefined;
            }
            const response = await questionnaireService.getInstance(questionnaireIds, activityInstanceId);

            if (!response.hasErrors) {
                return response.value.questionnaireInstance;
            }
            return undefined;
        },
        {
            enabled: !!activityInstanceId && questionnaireIds.length > 0,
            refetchOnWindowFocus: false,
            staleTime: ThreeMinutes,
        }
    );

    const questionnaireInstance = instance ?? questionnaireAssociationsActiveOnly?.[0];

    // ** Mutations */

    const createQuestionnaireInstanceMutation = useMutation(
        async (activityOwnerGuid: string) => {
            const isSelectedQuestionnaireActive = questionnaireAssociationsActiveOnly.some(
                (item) => item.questionnaireId === selectedQuestionnaireId
            );
            const questionnaireId = isSelectedQuestionnaireActive
                ? selectedQuestionnaireId
                : questionnaireInstance?.questionnaireId;

            if (!questionnaireId || !activityInstanceId) {
                return Promise.reject(`${!questionnaireId ? "Questionnaire" : "Activity Instance"} Id is missing`);
            }

            return questionnaireService.createInstance({
                activityInstanceId,
                questionnaireId,
                ownedBy: activityOwnerGuid,
            });
        },
        {
            onSuccess: async (response) => {
                if (!response.hasErrors) {
                    await queryClient.invalidateQueries([QueryKeys.QuestionnaireInstanceCurrent]);
                }
            },
        }
    );

    const updateQuestionnaireInstanceMutation = useMutation(
        async (updatedAnswer: Omit<IAnswer, "answerId">) => {
            // validate the date against the schema
            const parsedQuestionnaireInstanceResult = IQuestionnaireInstanceSchema.safeParse(questionnaireInstance);

            if (!parsedQuestionnaireInstanceResult.success) {
                return Promise.reject("Questionnaire ID is missing");
            }

            const instance = parsedQuestionnaireInstanceResult.data;
            const model = { answer: updatedAnswer };
            return questionnaireService.updateInstance(instance.questionnaireInstanceId, model);
        },
        {
            onSuccess: async (response) => {
                if (!response.hasErrors) {
                    await queryClient.invalidateQueries([QueryKeys.QuestionnaireInstanceCurrent]);
                }
            },
        }
    );

    const completeQuestionnaireInstanceMutation = useMutation(
        async () => {
            // validate the date against the schema
            const parsedQuestionnaireInstanceResult = IQuestionnaireInstanceSchema.safeParse(questionnaireInstance);

            if (!parsedQuestionnaireInstanceResult.success) {
                return Promise.reject("Questionnaire ID is missing");
            }

            const instance = parsedQuestionnaireInstanceResult.data;
            return await questionnaireService.completeInstance(instance.questionnaireInstanceId);
        },
        {
            onSuccess: async (response) => {
                if (!response.hasErrors) {
                    await queryClient.invalidateQueries([QueryKeys.QuestionnaireInstanceCurrent]);
                }
            },
        }
    );

    // ** Handlers ** //

    const handleGetStarted = async (activityOwnerGuid) => {
        if (!questionnaireInstance) {
            return;
        }

        await createQuestionnaireInstanceMutation.mutateAsync(activityOwnerGuid);
    };

    const handleAnswerQuestion = async (questionId: string, choiceId: string) => {
        const updatedAnswer = { questionId, choiceId };
        await updateQuestionnaireInstanceMutation.mutateAsync(updatedAnswer);
    };

    const handleComplete = async () => {
        const result = await completeQuestionnaireInstanceMutation.mutateAsync();
        return result.value.score;
    };

    // ** Effects ** //

    return {
        isFetching: isFetchingQuestionnaireInstance,
        isFetchingMinimal: isFetchingMinimalQuestionnaires,
        isLoadingCreateQuestionnaire: createQuestionnaireInstanceMutation.isLoading || isFetchingQuestionnaireInstance,
        questionnaireInstance,
        onAnswerQuestion: handleAnswerQuestion,
        onComplete: handleComplete,
        onGetStarted: handleGetStarted,
        questionnaireAssociations: questionnaireAssociations,
        questionnaireAssociationsActive: questionnaireAssociationsActiveOnly,
    };
};

export default useQuestionnaireInstance;
